import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';

import type { WorkspaceUserDto } from '../../types/workspace.types';

const ROLE_BADGE_CLASSES: Record<string, string> = {
  Admin: 'bg-purple-100 text-purple-700',
  Manager: 'bg-blue-100   text-blue-700',
  Finance: 'bg-green-100  text-green-700',
  Viewer: 'bg-gray-100   text-gray-700',
};

interface Props {
  user: WorkspaceUserDto;
  currentUserId?: string;
  onEditRole?: (user: WorkspaceUserDto) => void;
  onRemoveUser?: (user: WorkspaceUserDto) => void;
}

const WorkspaceUserRow: React.FC<Props> = ({
  user,
  currentUserId,
  onEditRole,
  onRemoveUser,
}) => {
  const badgeClass =
    ROLE_BADGE_CLASSES[user.applicationRole] ?? 'bg-gray-100 text-gray-700';

  const isSelf = user.userId === currentUserId;

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50">
      <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">
        {user.fullName}
      </td>
      <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">
        {user.email}
      </td>
      <td className="py-3 px-4">
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badgeClass}`}
        >
          {user.applicationRole}
        </span>
      </td>
      {(onEditRole || onRemoveUser) && (
        <td className="py-3 px-4 text-right whitespace-nowrap width-auto">
          <div className="relative inline-block text-left">
            <Menu as="div">
              <MenuButton className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 inline-flex items-center justify-center alignment-baseline">
                <span className="sr-only">Row actions</span>
                <EllipsisVerticalIcon className="w-5 h-5" aria-hidden="true" />
              </MenuButton>

              <MenuItems
                anchor="bottom end"
                className="w-40 bg-white border border-gray-200 rounded-md shadow-lg z-50 py-1 focus:outline-none"
              >
                <MenuItem>
                  <button
                    type="button"
                    disabled={isSelf}
                    onClick={() => onEditRole?.(user)}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Edit Role
                  </button>
                </MenuItem>
                <MenuItem>
                  <button
                    type="button"
                    disabled={isSelf}
                    onClick={() => onRemoveUser?.(user)}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Remove User
                  </button>
                </MenuItem>
              </MenuItems>
            </Menu>
          </div>
        </td>
      )}
    </tr>
  );
};

export default WorkspaceUserRow;
